import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const AdminManagement = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    // Check authentication first
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }
    
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      
      // Using simple version that matches your admin table structure
      const response = await fetch(`${API_BASE_URL}/api/admin_management_simple.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setAdmins(data.admins || []);
      } else {
        setError(data.message || 'فشل في تحميل بيانات المشرفين');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setCurrentAdmin(admin);
      setFormData({
        name: admin.name || '',
        email: admin.email || '',
        password: '',
        role: admin.role || 'admin'
      });
    } else {
      setCurrentAdmin(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'admin'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentAdmin(null);
    setFormData({ name: '', email: '', password: '', role: 'admin' });
    setShowPassword(false);
  };

  const handleSaveAdmin = async () => {
    try {
      const endpoint = `${API_BASE_URL}/api/admin_management_simple.php`;

      const requestData = {
        action: currentAdmin ? 'update' : 'create',
        ...formData
      };

      if (currentAdmin) {
        requestData.admin_id = currentAdmin.id;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(currentAdmin ? 'تم تحديث بيانات المشرف بنجاح' : 'تم إضافة مشرف جديد بنجاح');
        handleCloseDialog();
        loadAdmins();
      } else {
        setError(data.message || 'فشل في حفظ بيانات المشرف');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المشرف؟')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_management_simple.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'delete',
          admin_id: adminId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('تم حذف المشرف بنجاح');
        loadAdmins();
      } else {
        setError(data.message || 'فشل في حذف المشرف');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Remove the authentication check from here since it's now in useEffect
  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#4169E1' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AdminIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة المشرفين
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            إضافة مشرف جديد
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4169E1', mb: 1 }}>
            إدارة المشرفين
          </Typography>
          <Typography variant="body1" color="text.secondary">
            يمكنك من هنا إضافة وتعديل وحذف حسابات المشرفين في النظام
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AdminIcon sx={{ fontSize: 40, color: '#4169E1', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {admins.length}
                </Typography>
                <Typography color="text.secondary">
                  إجمالي المشرفين
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Admins Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الرقم</TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>البريد الإلكتروني</TableCell>
                  <TableCell>الدور</TableCell>
                  <TableCell>تاريخ الإنشاء</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      جاري التحميل...
                    </TableCell>
                  </TableRow>
                ) : admins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      لا توجد بيانات مشرفين
                    </TableCell>
                  </TableRow>
                ) : (
                  admins.map((admin, index) => (
                    <TableRow key={admin.id}>
                      <TableCell>{admin.id}</TableCell>
                      <TableCell>{admin.name}</TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            admin.role === 'super_admin' ? 'مشرف عام' :
                            admin.role === 'packaging_admin' ? 'مدير تغليف' :
                            admin.role === 'inventory_admin' ? 'مدير مخزون' :
                            'مشرف'
                          }
                          color={
                            admin.role === 'super_admin' ? 'primary' :
                            admin.role === 'packaging_admin' ? 'warning' :
                            admin.role === 'inventory_admin' ? 'info' :
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString('ar-EG') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(admin)}
                          title="تعديل"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAdmin(admin.id)}
                          title="حذف"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Add/Edit Admin Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentAdmin ? 'تعديل بيانات المشرف' : 'إضافة مشرف جديد'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="الاسم"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange('name')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="البريد الإلكتروني"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange('email')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={currentAdmin ? 'كلمة المرور الجديدة (اتركها فارغة إذا لم تريد تغييرها)' : 'كلمة المرور'}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={formData.password}
            onChange={handleInputChange('password')}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <HideIcon /> : <ViewIcon />}
                </IconButton>
              )
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="الدور"
            fullWidth
            variant="outlined"
            value={formData.role}
            onChange={handleInputChange('role')}
            SelectProps={{
              native: true
            }}
          >
            <option value="admin">مشرف</option>
            <option value="super_admin">مشرف عام</option>
            <option value="packaging_admin">مدير تغليف</option>
            <option value="inventory_admin">مدير مخزون</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button onClick={handleSaveAdmin} variant="contained">
            {currentAdmin ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
